import { supabase } from './supabaseClient';

// Herramientas y Ayuda (sql/138): recursos GLOBALES iguales para todos los negocios
// (videos de YouTube "no listado", guías en PDF, plantillas de mensajes y enlaces).
// Todos leen; solo la admin de Kontrol escribe (RLS con es_admin_kontrol()).

// Módulos de Kontrol a los que se puede asignar un video. `vistas` son los valores de
// vistaActual en App.js donde aparece el botón "¿Cómo funciona?" de ese módulo.
export const MODULOS_AYUDA = [
  { clave: 'general', nombre: 'Primeros pasos', vistas: [] },
  { clave: 'inicio', nombre: 'Inicio', vistas: ['inicio'] },
  { clave: 'vender', nombre: 'Vender', vistas: ['pos'] },
  { clave: 'ventashoy', nombre: 'Ventas de hoy', vistas: ['ventashoy'] },
  { clave: 'metas', nombre: 'Mis metas', vistas: ['metas'] },
  { clave: 'inventario', nombre: 'Inventario', vistas: ['inventario', 'categorias', 'grupos', 'detalle'] },
  { clave: 'stockbajo', nombre: 'Stock bajo', vistas: ['stockbajo'] },
  { clave: 'pedidos', nombre: 'Pedidos', vistas: ['pedidos'] },
  { clave: 'catalogo', nombre: 'Catálogo en línea', vistas: [] },
  { clave: 'servicios', nombre: 'Servicios', vistas: ['servicios'] },
  { clave: 'cotizaciones', nombre: 'Cotizaciones', vistas: ['cotizaciones'] },
  { clave: 'agenda', nombre: 'Agenda', vistas: ['agenda'] },
  { clave: 'ordenes', nombre: 'Órdenes de servicio', vistas: ['ordenes'] },
  { clave: 'balance', nombre: 'Balance', vistas: ['balance'] },
  { clave: 'cuentas', nombre: 'Cuentas', vistas: ['cuentas'] },
  { clave: 'gastos', nombre: 'Gastos', vistas: ['gastos'] },
  { clave: 'porcobrar', nombre: 'Por cobrar', vistas: ['porcobrar'] },
  { clave: 'corte', nombre: 'Corte del día', vistas: ['corte'] },
  { clave: 'cierre', nombre: 'Cierre de mes', vistas: ['cierre'] },
  { clave: 'facturas', nombre: 'Facturas', vistas: ['facturas'] },
  { clave: 'reportes', nombre: 'Reportes', vistas: ['reportes'] },
  { clave: 'contenido', nombre: 'Contenido con IA', vistas: ['contenido'] },
  { clave: 'clientes', nombre: 'Clientes', vistas: ['clientes'] },
  { clave: 'equipo', nombre: 'Mi equipo', vistas: ['equipo'] },
  { clave: 'planes', nombre: 'Mi plan', vistas: ['planes'] },
  { clave: 'config', nombre: 'Configuración', vistas: ['config'] },
];

export const nombreModulo = (clave) => MODULOS_AYUDA.find((m) => m.clave === clave)?.nombre || 'Primeros pasos';
export const moduloDeVista = (vista) => MODULOS_AYUDA.find((m) => m.vistas.includes(vista))?.clave || null;

// ID de un video de YouTube a partir de cualquier enlace común
// (youtu.be/ID, watch?v=ID, shorts/ID, embed/ID, live/ID). null si no es de YouTube.
export function youtubeId(url) {
  if (!url) return null;
  try {
    const u = new URL(url.trim());
    const host = u.hostname.replace(/^www\.|^m\./, '');
    let id = null;
    if (host === 'youtu.be') id = u.pathname.slice(1).split('/')[0];
    else if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
      id = u.searchParams.get('v');
      if (!id) {
        const m = u.pathname.match(/^\/(?:shorts|embed|live)\/([^/?#]+)/);
        id = m ? m[1] : null;
      }
    }
    return id && /^[\w-]{6,20}$/.test(id) ? id : null;
  } catch {
    return null;
  }
}

export const urlEmbebida = (id) => `https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1&playsinline=1`;
export const miniatura = (id) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;

// Los recursos cambian poco: se piden una vez por sesión y se comparten entre pantallas.
let cache = null;
let pendiente = null;

export function obtenerRecursosAyuda({ forzar = false } = {}) {
  if (cache && !forzar) return Promise.resolve(cache);
  if (pendiente && !forzar) return pendiente;
  pendiente = supabase
    .from('recursos_ayuda')
    .select('id, tipo, titulo, descripcion, modulo, url, contenido, orden, activo')
    .order('orden', { ascending: true })
    .order('creado_en', { ascending: true })
    .then(({ data, error }) => {
      pendiente = null;
      if (error) throw error;
      cache = data || [];
      return cache;
    }, (e) => { pendiente = null; throw e; });
  return pendiente;
}

const limpiar = (r) => ({
  tipo: r.tipo,
  titulo: (r.titulo || '').trim(),
  descripcion: (r.descripcion || '').trim() || null,
  modulo: r.modulo || 'general',
  url: (r.url || '').trim() || null,
  contenido: (r.contenido || '').trim() || null,
  orden: Number.isFinite(Number(r.orden)) ? Number(r.orden) : 0,
  activo: r.activo !== false,
  actualizado_en: new Date().toISOString(),
});

export async function guardarRecursoAyuda(recurso) {
  const datos = limpiar(recurso);
  const consulta = recurso.id
    ? supabase.from('recursos_ayuda').update(datos).eq('id', recurso.id)
    : supabase.from('recursos_ayuda').insert(datos);
  const { error } = await consulta;
  if (error) throw error;
  cache = null;
}

export async function borrarRecursoAyuda(id) {
  const { error } = await supabase.from('recursos_ayuda').delete().eq('id', id);
  if (error) throw error;
  cache = null;
}

// Sube un PDF de guía al bucket público 'recursos-ayuda' y devuelve su enlace.
export async function subirPdfAyuda(archivo) {
  if (!archivo) throw new Error('Elige un archivo PDF');
  if (archivo.type !== 'application/pdf') throw new Error('Solo se aceptan archivos PDF');
  if (archivo.size > 20 * 1024 * 1024) throw new Error('El PDF pesa más de 20 MB');
  const nombre = archivo.name.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9.]+/g, '-');
  const ruta = `guias/${Date.now()}-${nombre}`;
  const { error } = await supabase.storage.from('recursos-ayuda').upload(ruta, archivo, { contentType: 'application/pdf', upsert: false });
  if (error) throw error;
  return supabase.storage.from('recursos-ayuda').getPublicUrl(ruta).data.publicUrl;
}
